/**
 * Shared access-token refresh, used by two callers with different stakes:
 *
 *   - `api/client.ts` response interceptor — reactive: a request already
 *     401'd, refresh once and replay.
 *   - `realtime/RealtimeProvider` — proactive: the websocket handshake
 *     carries the JWT in the URL and the server rejects an expired one
 *     with HTTP 403 *before* the upgrade, which the browser reports as a
 *     bare close(1006) — indistinguishable from a network drop, so the
 *     client used to retry the same dead token on every backoff tick
 *     (260 rejected handshakes in one staging week, all noise in the
 *     `auth.failed` alert metric). Checking `exp` and refreshing BEFORE
 *     connecting is the only place that loop can be broken.
 *
 * Uses raw axios, not `apiClient`: the interceptor must never recurse
 * into itself through its own refresh call.
 */

import axios from "axios"

import { authStorage } from "./storage"

import { env } from "@/env"

// Refresh when the token dies within this window. Generous on purpose:
// the websocket URL token is validated server-side at handshake time,
// and a token that expires mid-flight between build-URL and accept
// produces exactly the 403 this exists to prevent.
const EXPIRY_SKEW_MS = 30_000

/** Best-effort read of the JWT `exp` claim; null when unparseable. */
function tokenExpiryMs(token: string): number | null {
  const payload = token.split(".")[1]
  if (!payload) return null
  try {
    const decoded = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))) as {
      exp?: number
    }
    return typeof decoded.exp === "number" ? decoded.exp * 1000 : null
  } catch {
    return null
  }
}

/**
 * Mint a new access token from the stored refresh token.
 * Stores and returns it on success; returns null on any failure.
 *
 * Deliberately does NOT clear storage or redirect — what a dead refresh
 * token means is the caller's decision (the axios interceptor logs the
 * user out; the realtime client just stays closed and lets the next API
 * call make that call).
 */
let inflight: Promise<string | null> | null = null

export function refreshAccessToken(): Promise<string | null> {
  // Single-flight: a page that fires five requests on an expired access
  // token gets one refresh, not five racing rotations of the refresh token.
  inflight ??= doRefresh().finally(() => {
    inflight = null
  })
  return inflight
}

async function doRefresh(): Promise<string | null> {
  const refresh = authStorage.getRefresh()
  if (!refresh) return null
  try {
    const { data } = await axios.post<{ access: string; refresh: string }>(
      `${env.VITE_API_BASE_URL}/api/auth/refresh`,
      { refresh },
    )
    authStorage.setAccess(data.access)
    // Rotated server-side: storing it is what keeps an active session alive
    // past the 7 days after login. Until 2026-10 neither half rotated.
    authStorage.setRefresh(data.refresh)
    return data.access
  } catch {
    return null
  }
}

/**
 * The stored access token, refreshed first if it is expired or about to
 * be. Returns null when there is no usable token (not logged in, or the
 * refresh token itself is dead).
 */
export async function getFreshAccessToken(): Promise<string | null> {
  const access = authStorage.getAccess()
  if (!access) return null
  const expiry = tokenExpiryMs(access)
  // Unparseable exp → hand it over as-is and let the server judge.
  if (expiry === null || expiry - Date.now() > EXPIRY_SKEW_MS) return access
  return refreshAccessToken()
}
